import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Badge,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  DirectionsBoat as PoweredVesselIcon,
  Sailing as NonPoweredVesselIcon,
  LocalShipping as TrailerIcon,
  Build as AccessoryIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  Schedule as MaintenanceIcon,
  TrendingDown as LowStockIcon,
  Assignment as CertificationIcon,
  Visibility as ViewAllIcon,
  Dashboard as DashboardIcon,
  Security as SafetyIcon
} from '@mui/icons-material';
import { getActiveVesselsByPropulsionType } from '../../../firebase/services/vesselService';
import { getActiveAccessories, getAccessoryStats } from '../../../firebase/services/accessoryService';
import { getTrailers } from '../../../firebase/services/trailerService';
import { Vessel, Accessory, Trailer } from '../../../models/types';

interface EquipmentStats {
  poweredVessels: {
    total: number;
    active: number;
    maintenance: number;
    needingAttention: number;
  };
  nonPoweredVessels: {
    total: number;
    active: number;
    maintenance: number;
    needingAttention: number;
  };
  trailers: {
    total: number;
    active: number;
    maintenanceDue: number;
    registrationDue: number;
  };
  accessories: {
    total: number;
    active: number;
    lowStock: number;
    maintenanceDue: number;
  };
}

const EquipmentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<EquipmentStats>({
    poweredVessels: { total: 0, active: 0, maintenance: 0, needingAttention: 0 },
    nonPoweredVessels: { total: 0, active: 0, maintenance: 0, needingAttention: 0 },
    trailers: { total: 0, active: 0, maintenanceDue: 0, registrationDue: 0 },
    accessories: { total: 0, active: 0, lowStock: 0, maintenanceDue: 0 }
  });

  useEffect(() => {
    loadAllEquipmentStats();
  }, []);

  const loadAllEquipmentStats = async () => {
    try {
      setLoading(true);
      
      // Load all equipment data in parallel
      const [poweredVessels, nonPoweredVessels, trailers, accessories, accessoryStats] = await Promise.all([
        getActiveVesselsByPropulsionType('powered'),
        getActiveVesselsByPropulsionType('non-powered'),
        getTrailers(),
        getActiveAccessories(),
        getAccessoryStats()
      ]);

      // Calculate powered vessel stats
      const poweredStats = calculateVesselStats(poweredVessels);
      
      // Calculate non-powered vessel stats
      const nonPoweredStats = calculateVesselStats(nonPoweredVessels);
      
      // Calculate trailer stats
      const trailerStats = calculateTrailerStats(trailers);
      
      // Use accessory stats from service
      const accessoryStatsCombined = {
        total: accessoryStats.total,
        active: accessoryStats.active,
        lowStock: accessoryStats.lowStock,
        maintenanceDue: accessoryStats.needingMaintenance
      };

      setStats({
        poweredVessels: poweredStats,
        nonPoweredVessels: nonPoweredStats,
        trailers: trailerStats,
        accessories: accessoryStatsCombined
      });

    } catch (err) {
      console.error('Error loading equipment stats:', err);
      setError('Failed to load equipment data');
    } finally {
      setLoading(false);
    }
  };

  const calculateVesselStats = (vessels: Vessel[]) => {
    const total = vessels.length;
    const active = vessels.filter(v => v.status === 'active').length;
    const maintenance = vessels.filter(v => v.status === 'maintenance').length;
    
    // Calculate vessels needing attention
    const now = new Date();
    const oneMonthFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const needingAttention = vessels.filter(vessel => {
      // Check if maintenance is due
      if (vessel.nextMaintenanceDue && new Date(vessel.nextMaintenanceDue) <= oneMonthFromNow) {
        return true;
      }
      // Check if certifications are expiring
      if (vessel.certifications) {
        return vessel.certifications.some(cert => 
          new Date(cert.expiryDate) <= oneMonthFromNow
        );
      }
      return false;
    }).length;

    return { total, active, maintenance, needingAttention };
  };

  const calculateTrailerStats = (trailers: Trailer[]) => {
    const total = trailers.length;
    const active = trailers.filter(t => t.isActive).length;
    
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    const maintenanceDue = trailers.filter(t => 
      t.nextMaintenanceDate && new Date(t.nextMaintenanceDate) <= thirtyDaysFromNow
    ).length;
    
    const registrationDue = trailers.filter(t => {
      if (!t.stateRegistrationExpiry) return false;
      const expiryDate = new Date(t.stateRegistrationExpiry);
      return expiryDate >= now && expiryDate <= thirtyDaysFromNow;
    }).length;

    return { total, active, maintenanceDue, registrationDue };
  };

  const getTotalEquipment = () => {
    return stats.poweredVessels.total + stats.nonPoweredVessels.total + 
           stats.trailers.total + stats.accessories.total;
  };

  const getTotalAlerts = () => {
    return stats.poweredVessels.needingAttention + stats.nonPoweredVessels.needingAttention +
           stats.trailers.maintenanceDue + stats.trailers.registrationDue +
           stats.accessories.lowStock + stats.accessories.maintenanceDue;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Equipment Dashboard
          </Typography>
        </Box>
        <Badge badgeContent={getTotalAlerts()} color="error">
          <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Badge>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #E3F2FD 0%, #FFFFFF 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PoweredVesselIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" color="primary.main">
                    Powered Vessels
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.poweredVessels.total}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`${stats.poweredVessels.active} Active`} color="success" size="small" />
                <Chip label={`${stats.poweredVessels.maintenance} Maintenance`} color="warning" size="small" />
              </Stack>
              {stats.poweredVessels.needingAttention > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                  <WarningIcon fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {stats.poweredVessels.needingAttention} need attention
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                onClick={() => navigate('/admin/equipment/vessels/powered')}
                startIcon={<ViewAllIcon />}
              >
                View All
              </Button>
              <Button 
                size="small" 
                onClick={() => navigate('/admin/equipment/vessels/new?propulsionType=powered')}
                startIcon={<AddIcon />}
              >
                Add New
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #E8F5E9 0%, #FFFFFF 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NonPoweredVesselIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" color="success.main">
                    Non-Powered Vessels
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.nonPoweredVessels.total}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`${stats.nonPoweredVessels.active} Active`} color="success" size="small" />
                <Chip label={`${stats.nonPoweredVessels.maintenance} Maintenance`} color="warning" size="small" />
              </Stack>
              {stats.nonPoweredVessels.needingAttention > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                  <WarningIcon fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {stats.nonPoweredVessels.needingAttention} need attention
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                onClick={() => navigate('/admin/equipment/vessels/nonpowered')}
                startIcon={<ViewAllIcon />}
              >
                View All
              </Button>
              <Button 
                size="small" 
                onClick={() => navigate('/admin/equipment/vessels/new?propulsionType=non-powered')}
                startIcon={<AddIcon />}
              >
                Add New
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #FFF3E0 0%, #FFFFFF 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrailerIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" color="warning.main">
                    Trailers
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.trailers.total}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`${stats.trailers.active} Active`} color="success" size="small" />
              </Stack>
              <Stack spacing={1}>
                {stats.trailers.maintenanceDue > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                    <MaintenanceIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {stats.trailers.maintenanceDue} maintenance due
                    </Typography>
                  </Box>
                )}
                {stats.trailers.registrationDue > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                    <CertificationIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {stats.trailers.registrationDue} registration due
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                onClick={() => navigate('/admin/equipment/trailers')}
                startIcon={<ViewAllIcon />}
              >
                View All
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #F3E5F5 0%, #FFFFFF 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccessoryIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" color="secondary.main">
                    Accessories & Safety
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.accessories.total}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`${stats.accessories.active} Active`} color="success" size="small" />
              </Stack>
              <Stack spacing={1}>
                {stats.accessories.lowStock > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                    <LowStockIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {stats.accessories.lowStock} low stock
                    </Typography>
                  </Box>
                )}
                {stats.accessories.maintenanceDue > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                    <MaintenanceIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {stats.accessories.maintenanceDue} maintenance due
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                onClick={() => navigate('/admin/equipment/accessories')}
                startIcon={<ViewAllIcon />}
              >
                View All
              </Button>
              <Button 
                size="small" 
                onClick={() => navigate('/admin/equipment/accessories/new')}
                startIcon={<AddIcon />}
              >
                Add New
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Overall Summary */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Equipment Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {getTotalEquipment()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Equipment Items
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="success.main" fontWeight="bold">
                    {stats.poweredVessels.active + stats.nonPoweredVessels.active + stats.trailers.active + stats.accessories.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Items
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="warning.main" fontWeight="bold">
                    {stats.poweredVessels.maintenance + stats.nonPoweredVessels.maintenance}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Maintenance
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="error.main" fontWeight="bold">
                    {getTotalAlerts()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Need Attention
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PoweredVesselIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Add Powered Vessel" 
                  secondary="Add new motorboat or yacht"
                />
                <IconButton 
                  onClick={() => navigate('/admin/equipment/vessels/new?propulsionType=powered')}
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <NonPoweredVesselIcon color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="Add Non-Powered Vessel" 
                  secondary="Add kayak, canoe, or sailboat"
                />
                <IconButton 
                  onClick={() => navigate('/admin/equipment/vessels/new?propulsionType=non-powered')}
                  color="success"
                >
                  <AddIcon />
                </IconButton>
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <SafetyIcon color="secondary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Add Safety Equipment" 
                  secondary="Add life jackets, flares, etc."
                />
                <IconButton 
                  onClick={() => navigate('/admin/equipment/accessories/new')}
                  color="secondary"
                >
                  <AddIcon />
                </IconButton>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EquipmentDashboard; 